import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { NODE_HOST_URL_v1 } from "../api/Api";
import {
  FiFileText,
  FiCheck,
  FiAlertTriangle,
  FiXCircle,
  FiList,
  FiPlus,
  FiZap,
} from "react-icons/fi";
import styles from "./TemplatesCard.module.css";
import Axios from "../../../axios/Axios";


const TemplatesCard = ({ selectedChannel, onChannelChange }) => {
  const lStorage = JSON.parse(localStorage.getItem("login"));
  const userId = lStorage?.user?.userId;
  const token = lStorage?.token;

  const [cardData, setCardData] = useState(null);

  const normalizeData = (data) => {
    const total = data?.totalTemplates ?? 0;
    const approved = data?.approvedTempaltes ?? 0;
    const rejected = data?.rejectedTempaltes ?? 0;

    let pending = total - (approved + rejected);

    // prevent negative (backend inconsistency)
    if (pending < 0) pending = 0;

    return { total, approved, rejected, pending };
  };

  const fetchtemplateCount = async (channel) => {
    try {
      const res = await Axios.post(`${NODE_HOST_URL_v1}/TemplateAnalytics`,
        {
          user_id: userId,
          method: "FetchTemplateCount",
          filter: channel
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        });
      setCardData(normalizeData(res.data));
    }
    catch (error) {
      setCardData(null);
    }
  }

  useEffect(() => {
    fetchtemplateCount(selectedChannel);
  }, [selectedChannel]);

  const showStatusCards = !["email", "sms", "voice", "rcs"].includes(selectedChannel);

  const channelTips = {
    email:  { headline: "Personalization boosts open rates",   body: "Templates with first-name variables see up to 26% higher open rates. Keep subject lines under 50 characters for best deliverability." },
    sms:    { headline: "Stay within 160 characters",          body: "Messages over 160 characters are split into multiple SMS and billed accordingly. Use URL shorteners to save space and track clicks." },
    voice:  { headline: "Short scripts perform better",        body: "Voice templates under 30 seconds have significantly higher completion rates. Use clear pauses and avoid complex sentences." },
    rcs:    { headline: "Rich cards drive higher engagement",  body: "RCS templates with images or action buttons see up to 45% more interaction than plain text. Use suggested replies to guide responses." },
  };

  return (
    <div>
      {/* HEADER */}
      <div className={`d-flex align-items-start mb-2 ${styles.pageHeader}`}>
        <div>
          <div className={styles.pageTitle}>Template Manager</div>
          <div className={styles.pageSubtitle}>
            Create, approve, and analyze message templates across all channels.
          </div>
        </div>

        <div className="qw-btn-group ">
          <Link to={`/dashboard/template-view?channel=${selectedChannel.toUpperCase()}`} className="qw-btn qw-btn--soft">
            <span className="qw-btn__icon">
              <FiList size={16} />
            </span>
            View Template List
          </Link>

          <Link to={`/dashboard/template-create?channel=${selectedChannel.toUpperCase()}`} className="qw-btn qw-btn--primary ml-2">
            <span className="qw-btn__icon">
              <FiPlus size={16} />
            </span>
            Create New Template
          </Link>
        </div>
      </div>

      <div className={`d-flex mb-2 ${styles.channelTabsWrapper}`}>
        <div className={styles.channelTabs}>
          {["whatsapp", "email", "sms", "voice", "rcs"].map((ch) => (
            <div
              key={ch}
              className={`${styles.tab} ${selectedChannel === ch ? styles.activeTab : ""
                }`}
              onClick={() => onChannelChange(ch)}
            >
              {ch}
            </div>
          ))}
        </div>
      </div>
      {/* STATS */}
      <div className="row g-4">
        {/* TOTAL */}
        {showStatusCards ? (
          <div className="col-xl-3 col-md-6">
            <div className={styles.statCard}>
              <div className={styles.cardTop}>
                <div>
                  <div className={styles.cardLabel}>Total Templates</div>
                  <div className={styles.cardValue}>
                    {cardData?.total ?? "--"}
                  </div>
                </div>
                <div className={`${styles.iconBox} ${styles.total}`}>
                  <FiFileText size={16} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="col-xl-4 col-md-12">
              <div className={styles.statCard}>
                <div className={styles.cardTop}>
                  <div>
                    <div className={styles.cardLabel}>Total Templates</div>
                    <div className={styles.cardValue}>{cardData?.total ?? "--"}</div>
                  </div>
                  <div className={`${styles.iconBox} ${styles.total}`}>
                    <FiFileText size={16} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-8 col-md-12">
              <div className={styles.tipCard}>
                <div className={`${styles.iconBox} ${styles.tipIcon}`}>
                  <FiZap size={18} />
                </div>
                <div>
                  <div className={styles.tipHeadline}>{channelTips[selectedChannel]?.headline}</div>
                  <div className={styles.tipBody}>{channelTips[selectedChannel]?.body}</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* APPROVED / PENDING / REJECTED — whatsapp only */}
        {showStatusCards && (
          <>
            <div className="col-xl-3 col-md-6">
              <div className={styles.statCard}>
                <div className={styles.cardTop}>
                  <div>
                    <div className={styles.cardLabel}>Approved</div>
                    <div className={styles.cardValue}>
                      {selectedChannel === "whatsapp"
                        ? cardData?.approved ?? "--"
                        : "--"}
                    </div>
                  </div>
                  <div className={`${styles.iconBox} ${styles.approved}`}>
                    <FiCheck size={16} />
                  </div>
                </div>
                <div className={styles.cardFooter} />
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className={styles.statCard}>
                <div className={styles.cardTop}>
                  <div>
                    <div className={styles.cardLabel}>Pending</div>
                    <div className={styles.cardValue}>
                      {selectedChannel === "whatsapp"
                        ? cardData?.pending ?? "--"
                        : "--"}
                    </div>
                  </div>
                  <div className={`${styles.iconBox} ${styles.pending}`}>
                    <FiAlertTriangle size={16} />
                  </div>
                </div>
                <div className={styles.cardFooter} />
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className={styles.statCard}>
                <div className={styles.cardTop}>
                  <div>
                    <div className={styles.cardLabel}>Rejected</div>
                    <div className={styles.cardValue}>
                      {selectedChannel === "whatsapp"
                        ? cardData?.rejected ?? "--"
                        : "--"}
                    </div>
                  </div>
                  <div className={`${styles.iconBox} ${styles.rejected}`}>
                    <FiXCircle size={16} />
                  </div>
                </div>
                <div className={styles.cardFooter} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TemplatesCard;
